import { Async } from '@client/new/helpers/types';
import {
  BundlePricing,
  CreateOtpBundleInput,
  CreateOtpBundleMutation,
  DeleteOtpBundleMutation,
  Exact,
  GetInfluencerQuery,
  GetOtpBundlesQuery,
  UpdateOtpBundleInput,
  UpdateOtpBundleMutation,
  useCreateOtpBundleMutation,
  useDeleteOtpBundleMutation,
  useGetOtpBundlesQuery,
  useGetSellableContentItemsQuery,
  useUpdateOtpBundleMutation,
} from 'generated/graphql';
import {
  AllFeatures,
  RawPrograms,
  AllPrograms,
  AllContent,
  RawContentItem,
  RawOtpBundle,
} from '../../common/bundle.types';
import {
  allowedContentTypes,
  toAllContent,
  toSellableContent,
} from '../../common/bundles.mapper';
import {
  toAllOtpPrograms,
  toSellableOtpPrograms,
} from './otpBundles.model.mapper';
import { useUser } from '@client/new/hooks/use-user';
import { useCustomizations } from '@client/new/hooks/use-customizations';
import {
  ApolloCache,
  ApolloQueryResult,
  DefaultContext,
  FetchResult,
  MutationFunctionOptions,
} from '@apollo/client';

export type FormMode = 'edit' | 'create' | 'duplicate' | null;

export type NewBundleSubmit = Array<{ id: string; title: string }>;
export type EditBundleSubmit = Array<{ id: number; title: string }>;

export type Discount = {
  code: string;
  id: string;
  isActive: boolean;
  name: string;
  paidProductIds?: number[] | null | undefined;
};
export type BundlesModelData = {
  allFeatures: AllFeatures;
  allContent: AllContent;
  sellableContent: AllContent;
  rawContent: RawContentItem[];
  allPrograms: AllPrograms;
  sellablePrograms: AllPrograms;
  otpBundles: RawOtpBundle[];
  discounts: Discount[];
  createOtpBundle: (
    options?:
      | MutationFunctionOptions<
          CreateOtpBundleMutation,
          Exact<{
            input: CreateOtpBundleInput;
          }>,
          DefaultContext,
          ApolloCache<any>
        >
      | undefined,
  ) => Promise<FetchResult<CreateOtpBundleMutation>>;
  updateOtpBundle: (
    options?:
      | MutationFunctionOptions<
          UpdateOtpBundleMutation,
          Exact<{
            input: UpdateOtpBundleInput;
          }>,
          DefaultContext,
          ApolloCache<any>
        >
      | undefined,
  ) => Promise<FetchResult<UpdateOtpBundleMutation>>;
  deleteOtpBundle: (
    options?:
      | MutationFunctionOptions<
          DeleteOtpBundleMutation,
          Exact<{
            bundleId: number;
          }>,
          DefaultContext,
          ApolloCache<any>
        >
      | undefined,
  ) => Promise<FetchResult<DeleteOtpBundleMutation>>;
  refetchOtpBundles: (
    variables?:
      | Partial<
          Exact<{
            creatorId: number;
            bundlePricing?: Array<BundlePricing | BundlePricing>;
          }>
        >
      | undefined,
  ) => Promise<ApolloQueryResult<GetOtpBundlesQuery>>;
  refetchInfluencer: (
    variables?:
      | Partial<
          Exact<{
            id: number;
          }>
        >
      | undefined,
  ) => Promise<ApolloQueryResult<GetInfluencerQuery>>;
  /**
   * Mirrors the creator customizations flag. The OTP VM gates readiness
   * UI/disable behavior on it so the legacy experience keeps the simpler
   * Draft/Published pill with no publication-criteria checks.
   */
  isUnifiedProgramsEnabled: boolean;
};

export type OtpBundlesModel = BundlesModelData;

export const useOtpBundlesModel = ({
  creatorId,
}: {
  creatorId: number;
}): Async<OtpBundlesModel> => {
  const { refetchInfluencer } = useUser(creatorId);
  const { customizations } = useCustomizations(String(creatorId));
  const isUnifiedProgramsEnabled =
    customizations?.isUnifiedProgramsEnabled ?? false;

  const [createOtpBundle] = useCreateOtpBundleMutation();
  const [updateOtpBundle] = useUpdateOtpBundleMutation();
  const [deleteOtpBundle] = useDeleteOtpBundleMutation();

  const {
    data,
    loading,
    error,
    refetch: refetchOtpBundles,
  } = useGetOtpBundlesQuery({
    variables: {
      creatorId: creatorId,
      bundlePricing: BundlePricing.Otp,
    },
    fetchPolicy: 'network-only',
  });

  const { data: contentData } = useGetSellableContentItemsQuery({
    variables: {
      creatorId: String(creatorId),
    },
    fetchPolicy: 'network-only',
  });

  const rawContent = (contentData?.content?.contentItems || []).filter((item) =>
    allowedContentTypes.includes(item.type),
  ) as RawContentItem[];

  const discounts = data?.getCouponsByInfluencer || [];
  const allPrograms = toAllOtpPrograms(data?.collections as RawPrograms);
  const allContent = rawContent ? toAllContent(rawContent) : [];
  const initialOtpBundles = (data?.bundle || []).filter(
    (b) => b.pricing === BundlePricing.Otp,
  ) as RawOtpBundle[];

  if (loading) {
    return {
      type: 'fetching',
    };
  }

  if (error) {
    return {
      type: 'fetch-error',
      value: 'Could not fetch bundle deatils',
    };
  }

  return {
    type: 'fetched',
    value: {
      otpBundles: initialOtpBundles,
      allPrograms,
      sellablePrograms: toSellableOtpPrograms(data?.collections as RawPrograms),
      allFeatures: data?.feature || [],
      allContent,
      sellableContent: rawContent ? toSellableContent(rawContent) : [],
      rawContent: rawContent || [],
      discounts,
      createOtpBundle,
      updateOtpBundle,
      deleteOtpBundle,
      refetchInfluencer,
      refetchOtpBundles,
      isUnifiedProgramsEnabled,
    },
  };
};
